import React, { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { Box, InputBase, makeStyles, Typography, withStyles } from '@material-ui/core';
import { DropDownIcon } from '../Icon';
import { classNames } from '../../utils';
import { Checkbox } from '../../page/DutyAssign/components/Attendance/Setting';
import { i18n } from '../../language';

const useStyles = makeStyles((theme) => ({
  inputBox: (props) => ({
    position: 'relative',
    height: props.height ? props.height : '3.5rem',
    width: props.width ? props.width : '100%',
    border: 'solid 1px #e4e7eb',
    borderRadius: '0.625rem',
    background: '#fff',
  }),
  inputBase: (props) => ({
    height: '100%',
    width: '100%',
    lineHeight: props.height ? `${props.height}rem` : '3.5rem',
    borderRadius: '0.625remm',
    paddingLeft: '1.25rem',
    paddingRight: props.dropDownIcon ? '2rem' : '0',
    '& .MuiInputBase-input': {
      fontSize: '1rem',
      color: '#2e4469',
      fontFamily: 'NotoSans-Medium',
      height: 'unset',
    },
  }),
  dropDownIcon: {
    position: 'absolute',
    top: '50%',
    right: '1rem',
    transform: 'translateY(-50%)',
    height: '0.875rem',
    width: '0.875rem',
    color: '#538fff',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  error: {
    borderColor: '#f44336 !important',
  },
  errorMsg: ({ errMsgMargin, errMsgWidth }) => ({
    margin: errMsgMargin,
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
    width: errMsgWidth,
  }),
}));

const AutoCompleteCheckbox = forwardRef(
  (
    {
      id,
      classes,
      itemList,
      className,
      placeholder,
      value,
      onChange,
      width,
      height,
      style,
      onOpen,
      onClose,
      onBlur,
      errorMsg,
      error,
      dropDownIcon,
      align = 'left',
      allText = 'All',
      errMsgMargin = '3px 0.825rem 0 0.825rem',
      errMsgWidth = 'auto',
      allItem = true,
      readOnly = false,
      open = undefined,
      endAdornment,
    },
    ref
  ) => {
    const styles = useStyles({ width, height, errMsgMargin, errMsgWidth, dropDownIcon });
    const [inputValue, setInputValue] = React.useState('');
    const allSelected = value.length === itemList.length;
    const filter = createFilterOptions();

    const handleReset = () => {
      setInputValue('');
    };

    useImperativeHandle(ref, () => ({
      handleReset,
    }));

    const handleSelectAll = useCallback(() => {
      if (allSelected) {
        onChange([]);
      } else {
        onChange(itemList);
      }
    }, [allSelected, itemList, onChange]);

    useEffect(() => {
      if (value.length === 1) {
        setInputValue(value[0].text);
      } else if (value.length > 1) {
        setInputValue(i18n.t('DUTYASSIGN_TEMPLATE_AUTO_RULE_SELECTED', { value: value.length }));
      }
    }, []);

    const getErroeClass = (props) => {
      return error || errorMsg ? classNames(props, styles.error) : props;
    };

    const inputBody = (params) => {
      const dropDownHandle = () => {
        params.inputProps.ref.current.childNodes[0].focus();
        params.inputProps.onMouseDown();
      };

      return (
        <div ref={params.InputProps.ref} className={getErroeClass(styles.inputBox)}>
          <InputBase
            {...params.inputProps}
            placeholder={placeholder}
            className={styles.inputBase}
            type="text"
            onBlur={(e) => {
              params.inputProps.onBlur(e);
              if (typeof onBlur === 'function') {
                onBlur();
              }
            }}
            readOnly={readOnly}
            endAdornment={endAdornment}
          />
          {dropDownIcon && <DropDownIcon className={styles.dropDownIcon} onClick={dropDownHandle} />}
        </div>
      );
    };

    const renderOptionCtr = (option, { selected, allSelected, align, margin }) => {
      const selectAllProps = option.value === 'select-all' ? { check: allSelected } : {};
      // 根据 align 和 margin 确定渲染的内容
      let content;
      if (align === 'left') {
        if (margin === '') {
          content = (
            <>
              <Checkbox check={selected} {...selectAllProps} />
              {option.text}
            </>
          );
        } else {
          content = (
            <Box display="flex" alignItems="center">
              <Checkbox check={selected} {...selectAllProps} />
              <Box margin={margin}>{option.text}</Box>
            </Box>
          );
        }
      } else {
        content = (
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100vw" p="0.5rem 0">
            {option.text}
            <Checkbox check={selected} {...selectAllProps} />
          </Box>
        );
      }

      // 返回渲染的内容
      return content;
    };

    // 組件本體onBlur事件
    const handleOnBlur = () => {
      if (value?.length === 1) {
        setInputValue(value[0].text);
      } else if (value?.length > 1) {
        setInputValue(
          i18n.t('DUTYASSIGN_TEMPLATE_AUTO_RULE_SELECTED', {
            value: value.length > itemList.length ? itemList.length : value.length,
          })
        );
      }
    };

    // 組件本體onFocus事件
    const handleOnFocus = () => {
      setInputValue('');
    };

    const isSelectAll = (newValue) => newValue.find((option) => option.value === 'select-all');

    const handleValueChange = (newValue) => {
      if (typeof onChange === 'function') {
        onChange(newValue);
      }
    };

    // 選擇item事件
    const handleOnChange = (event, newValue, reason) => {
      if (reason === 'select-option' || reason === 'remove-option') {
        if (isSelectAll(newValue)) {
          handleSelectAll();
        } else {
          handleValueChange(newValue);
        }
      }
    };

    // input框事件
    const handleInputChange = (event, newInputValue, reason) => {
      if (reason === 'reset') {
        return;
      }
      setInputValue(newInputValue);
    };

    // 獲取list item的文本
    const hanldeGetOptionLabel = (option) => option.text;

    // 獲取list item的選擇狀態
    const hanldeGetOptionSelected = (option, anotherOption) => option.value === anotherOption.value;

    // 處理過濾list item
    const hanldeFilterOptions = (options, params) => {
      const filtered = filter(options, params);
      return allItem ? [{ text: allText, value: 'select-all' }, ...filtered] : filtered;
    };

    // 手动控制弹窗开关的时候触发
    useEffect(() => {
      if (open === false) {
        handleOnBlur();
      } else if (open === true) {
        handleOnFocus();
      }
    }, [open]);

    return (
      <Box
        display="flex"
        style={style}
        className={className}
        flexDirection="column"
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
      >
        <Autocomplete
          id={id}
          open={open}
          multiple
          disableCloseOnSelect
          classes={classes}
          className={classNames(styles.root, className)}
          value={value}
          onChange={handleOnChange}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          options={Array.isArray(itemList) ? itemList : []}
          getOptionLabel={hanldeGetOptionLabel}
          renderInput={inputBody}
          style={style}
          renderOption={renderOptionCtr}
          onOpen={onOpen}
          onClose={onClose}
          getOptionSelected={hanldeGetOptionSelected}
          filterOptions={hanldeFilterOptions}
        />
        {errorMsg ? <Typography className={styles.errorMsg}>{errorMsg}</Typography> : ''}
      </Box>
    );
  }
);

export default withStyles({
  paper: {
    boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.1)',
  },
  listbox: {
    '& li[aria-selected="true"]': {
      color: 'unset',
      background: 'unset',
      cursor: 'pointer',
    },
    '& li[data-focus="true"]': {
      color: '#538fff',
      background: '#f5faff',
      cursor: 'pointer',
    },
  },
  option: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
    paddingRight: 0,
    '&>div:first-child': {
      marginRight: '0.75rem',
    },
  },
})(AutoCompleteCheckbox);
